import { pathOr } from 'ramda';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { IApplicationState } from '../../../../../types/redux';
import { IFeaturesMap } from '../../../../../types/map';
import { TMapBounds } from '../../../../../types/mapBounds';
import { geoJsonFeatureToRBushItem } from '../../../../../utils/features';
import { getTilesToUpdate } from '../../../../../utils/tiles';
import { useFeaturesContext } from '../context';

interface IFeaturesUpdaterProps {
  detailsVisible?: boolean;
}

export const FeaturesUpdater: React.FC<IFeaturesUpdaterProps> = ({ detailsVisible: isDetailsVisible }) => {
  const { rbush, pointsLayer, hotspotsLayer, balloonManager } = useFeaturesContext();
  const { bounds, zoom, precision } = useSelector<IApplicationState, TMapBounds>(state => state.mapBounds);
  const features = useSelector<IApplicationState, IFeaturesMap>(state =>
    pathOr<IFeaturesMap>({}, [precision, 'features'], state.features),
  );
  const loadedFeatures = React.useRef<IFeaturesMap>({});
  const resultsVersion = useSelector<IApplicationState, Symbol | undefined>(state => state.results.version);
  const isNewbuildingsListing = useSelector<IApplicationState, boolean>(state => state.isNewbuildingsListing);

  pointsLayer.isNewbuildingsListing = isNewbuildingsListing;

  React.useEffect(() => {
    rbush.clear();
    loadedFeatures.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsVersion, precision]);

  React.useEffect(() => {
    balloonManager.objectManager.removeAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsVersion]);

  React.useEffect(() => {
    const rbushItems = Object.values(features)
      .filter(({ id }) => !loadedFeatures.current[id])
      .map(geoJsonFeatureToRBushItem);

    if (rbushItems.length === 0) {
      if (Object.keys(loadedFeatures.current).length === 0) {
        pointsLayer.layer.update();
      }

      return;
    }

    rbush.load(rbushItems);

    const featuresWereLoaded = Object.keys(loadedFeatures.current).length > 0;
    loadedFeatures.current = features;

    hotspotsLayer.layer.update();

    if (isDetailsVisible) {
      balloonManager.updateBalloons();
    }

    if (!featuresWereLoaded) {
      pointsLayer.layer.update();

      return;
    }

    console.time('getTilesToUpdate');
    const tilesToUpdate = getTilesToUpdate({ rbushItems, bounds, zoom, includeShadow: isDetailsVisible });
    console.timeEnd('getTilesToUpdate');

    console.time('pointsLayer update');
    if (tilesToUpdate.length > 0) {
      pointsLayer.layer.update(tilesToUpdate);
    }
    console.timeEnd('pointsLayer update');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features]);

  React.useEffect(() => {
    if (isDetailsVisible) {
      balloonManager.updateBalloons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds]);

  React.useEffect(() => {
    if (isDetailsVisible) {
      balloonManager.updateBalloons();
    } else {
      balloonManager.objectManager.removeAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetailsVisible]);

  return null;
};
